/**
 * The readers under the page (Figma 00, 22:130; 0b): `ReadersStrip` is the band under the sheet (its title, the
 * note that the readers disagree), `ReaderLine` one reader (the reader of record's dark chip, or a witness's
 * outlined one, then its words, and where offered the act that takes those words), `Words` a reading with the
 * words that differ from the reader of record lit.
 */
import type { ReactNode } from 'react';
import { BORDER, RADIUS, SPACE, TEXT, WASH_ALPHA, WEIGHT, tint, usePalette } from '../theme';
import { Chip, INK, OUTLINE } from './chip';

export function ReadersStrip({ title, hint, children }: { title: ReactNode; hint?: ReactNode; children: ReactNode }) {
  const p = usePalette();
  return (
    <section
      aria-label="Readers beneath"
      style={{ flexShrink: 0, background: p.surface, borderTop: `1px solid ${p.hairline}`, display: 'flex', flexDirection: 'column', paddingBottom: SPACE.sm }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, padding: `${SPACE.snug}px ${SPACE.wide}px ${SPACE.tight}px` }}>
        <span style={{ fontSize: TEXT.control, fontWeight: WEIGHT.strong, color: p.ink }}>{title}</span>
        <span style={{ flex: 1 }} />
        {hint !== undefined && <span style={{ fontSize: TEXT.caption, color: p.inkMuted }}>{hint}</span>}
      </div>
      {children}
    </section>
  );
}

export function ReaderLine({ chip, record, action, children }: { chip: ReactNode; record: boolean; action?: ReactNode; children: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: SPACE.snug, padding: `${SPACE.nudge}px ${SPACE.wide}px` }}>
      <span style={{ flexShrink: 0 }}>
        <Chip look={record ? INK : OUTLINE} size="row">
          {chip}
        </Chip>
      </span>
      <span style={{ flex: 1, minWidth: 0, fontSize: TEXT.control, color: p.inkSecondary, lineHeight: 'normal' }}>{children}</span>
      {action}
    </div>
  );
}

/** A piece of a reading: its words, and whether they differ from the reader of record's. */
export type WordPart = { readonly text: string; readonly differs: boolean };

export function Words({ parts }: { parts: readonly WordPart[] }) {
  const p = usePalette();
  return (
    <>
      {parts.map((part, index) =>
        part.differs ? (
          <mark key={index} style={{ background: tint(p.absence, WASH_ALPHA.mark), color: p.ink, borderRadius: RADIUS.hair, padding: `0 ${BORDER.line}px` }}>
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}
